import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Flame, Trophy, Star, Zap, Crown, Award } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

const MILESTONE_ICONS = {
  'Week Warrior': Zap,
  'Fortnight Focus': Star,
  'Monthly Master': Trophy,
  'Dedicated Writer': Award,
  'Century Scribe': Crown,
  'Year of Words': Flame,
};

const MILESTONE_COLORS = {
  'Week Warrior': 'text-amber-500 bg-amber-50',
  'Fortnight Focus': 'text-blue-500 bg-blue-50',
  'Monthly Master': 'text-purple-500 bg-purple-50',
  'Dedicated Writer': 'text-brand-primary bg-brand-primary/10',
  'Century Scribe': 'text-brand-gold bg-brand-gold/10',
  'Year of Words': 'text-red-500 bg-red-50',
};

const StreakWidget = () => {
  const { api } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const res = await api.get('/auth/streak');
        setStreak(res.data);
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetchStreak();
  }, [api]);

  if (loading || !streak) return null;

  const currentStreak = streak.writing_streak || 0;
  const longestStreak = streak.longest_streak || 0;
  const milestonesReached = streak.milestones_reached || [];
  const allMilestones = streak.all_milestones || [];

  // Find next milestone
  const nextMilestone = allMilestones.find(m => !milestonesReached.includes(m.name));
  const progressToNext = nextMilestone
    ? Math.min((currentStreak / nextMilestone.days) * 100, 100)
    : 100;

  // Flame intensity based on streak
  const getFlameColor = (streak) => {
    if (streak >= 30) return 'text-red-500';
    if (streak >= 7) return 'text-orange-500';
    if (streak >= 1) return 'text-amber-500';
    return 'text-text-muted';
  };

  const getFlameBg = (streak) => {
    if (streak >= 30) return 'bg-red-50';
    if (streak >= 7) return 'bg-orange-50';
    if (streak >= 1) return 'bg-amber-50';
    return 'bg-surface-alt';
  };

  const flameColor = getFlameColor(currentStreak);
  const flameBg = getFlameBg(currentStreak);

  return (
    <Card className="bg-white border-brand-primary/10" data-testid="streak-widget">
      <CardContent className="p-6">
        {/* Streak Counter */}
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-14 h-14 rounded-2xl ${flameBg} flex items-center justify-center`}>
            <Flame className={`w-7 h-7 ${flameColor} ${currentStreak > 0 ? 'animate-pulse' : ''}`} />
          </div>
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-text-primary" data-testid="streak-count">
                {currentStreak}
              </span>
              <span className="text-sm text-text-muted">day{currentStreak !== 1 ? 's' : ''}</span>
            </div>
            <p className="text-sm text-text-muted">
              {currentStreak === 0
                ? 'Start your writing streak today!'
                : 'Writing streak'}
            </p>
          </div>
        </div>

        {/* Progress to next milestone */}
        {nextMilestone && currentStreak > 0 && (
          <div className="mb-4" data-testid="streak-progress">
            <div className="flex items-center justify-between text-xs text-text-muted mb-1.5">
              <span>Next: {nextMilestone.name}</span>
              <span>{currentStreak}/{nextMilestone.days} days</span>
            </div>
            <div className="h-2 bg-surface-alt rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progressToNext}%` }}
              />
            </div>
          </div>
        )}

        {/* Best streak */}
        {longestStreak > 0 && longestStreak !== currentStreak && (
          <p className="text-xs text-text-muted mb-4">
            Personal best: <span className="font-medium text-text-secondary">{longestStreak} days</span>
          </p>
        )}

        {/* Earned Milestones */}
        {milestonesReached.length > 0 && (
          <div data-testid="milestones-earned">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Milestones</p>
            <div className="flex flex-wrap gap-2">
              {milestonesReached.map((name) => {
                const Icon = MILESTONE_ICONS[name] || Star;
                const colors = MILESTONE_COLORS[name] || 'text-text-muted bg-surface-alt';
                return (
                  <span
                    key={name}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${colors}`}
                    title={allMilestones.find(m => m.name === name)?.description || name}
                    data-testid={`milestone-${name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-3 h-3" />
                    {name}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StreakWidget;
